import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FileText, Plus } from 'lucide-react';
import { format } from 'date-fns';

import PageHeader from '../components/PageHeader.jsx';
import { listQuotes } from '../api/quotes';

/**
 * Quotes list — paginated, newest first. Mirrors Sales.jsx.
 *
 * Each row shows the quote number, customer, date, status, and final price.
 * Tapping opens /quotes/:id; the FAB at bottom-right opens /quotes/new.
 */

// Backend enum (backend/app/models/quote.py): draft | submitted | accepted |
// rejected | converted. Pill colours signal stage, not severity:
//   - draft:     neutral grey, work-in-progress on rep's side
//   - submitted: blue, sent to customer, awaiting decision
//   - accepted:  green, customer signed off
//   - rejected:  red, customer declined
//   - converted: purple, became a sale (terminal happy state)
const STATUS_PILL = {
  draft:      'bg-gray-100 text-gray-700',
  submitted:  'bg-blue-100 text-blue-700',
  accepted:   'bg-green-100 text-green-700',
  rejected:   'bg-red-100 text-red-700',
  converted:  'bg-purple-100 text-purple-700',
};

// Filter chips. Order matches the lifecycle: draft → submitted → accepted
// → converted; rejected is the off-ramp. Page-load default is Draft (the
// most-active view for reps); tap-to-toggle on the active chip clears
// the filter back to "all quotes" if a wider browse is wanted.
const STATUS_FILTERS = [
  { value: 'draft',     label: 'Draft'     },
  { value: 'submitted', label: 'Submitted' },
  { value: 'accepted',  label: 'Accepted'  },
  { value: 'converted', label: 'Converted' },
  { value: 'rejected',  label: 'Rejected'  },
];

function StatusPill({ status }) {
  const cls = STATUS_PILL[status] ?? 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-pill text-caption font-medium capitalize ${cls}`}>
      {status?.replace(/_/g, ' ') ?? 'unknown'}
    </span>
  );
}

function fmt(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return format(new Date(iso), 'd MMM yyyy');
  } catch {
    return '';
  }
}

export default function Quotes() {
  const navigate = useNavigate();
  // Default to Draft on first land — same rationale as Sales: that's
  // the most-active view for reps. Tap-to-deselect the active chip
  // clears the filter back to "all quotes" if needed.
  const [statusFilter, setStatusFilter] = useState('draft');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listQuotes({
      page: 1,
      pageSize: 20,
      status: statusFilter ?? undefined,
    })
      .then((data) => {
        if (cancelled) return;
        setItems(data.items ?? []);
        setHasMore((data.page ?? 1) < (data.pages ?? 0));
        setPage(data.page ?? 1);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || err.message || 'Failed to load quotes');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [statusFilter]);

  async function loadMore() {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const data = await listQuotes({
        page: next,
        pageSize: 20,
        status: statusFilter ?? undefined,
      });
      setItems((curr) => [...curr, ...(data.items ?? [])]);
      setHasMore((data.page ?? next) < (data.pages ?? 0));
      setPage(data.page ?? next);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }

  // Pick up any flash from an incoming navigation — e.g. QuoteDetail's
  // post-delete redirect lands here with a confirmation. Same pattern
  // as Sales.jsx.
  const location = useLocation();
  const flash = location.state?.flash;

  return (
    <main className="min-h-full px-4 pb-24">
      <PageHeader title="Quotes" subtitle="Recent quotes, newest first" icon={FileText} />

      {flash && (
        <div role="status" className="mb-3 mb-card bg-green-50 border-green-200 text-small text-green-900">
          {flash}
        </div>
      )}

      {/* Status filter chips — same horizontal-scroll pattern as Sales.
          `no-scrollbar` hides the scrollbar chrome on Android Chrome /
          Firefox / desktop while keeping the row swipeable; iOS Safari
          already hides it by default. */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3 -mx-4 px-4">
        {STATUS_FILTERS.map((f) => {
          const active = statusFilter === f.value;
          return (
            <button
              key={f.label}
              type="button"
              onClick={() => setStatusFilter(active ? null : f.value)}
              className={`min-h-touch px-4 rounded-pill text-small font-medium whitespace-nowrap transition-colors ${
                active
                  ? 'bg-amber-500 text-white border border-amber-500'
                  : 'bg-surface border border-gray-300 text-gray-700'
              }`}
              aria-pressed={active}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {error && (
        <p role="alert" className="mb-3 text-small text-danger">
          {error}
        </p>
      )}

      <div>
        {loading ? (
          <p className="text-small text-gray-500">Loading quotes…</p>
        ) : items.length === 0 ? (
          <div className="mb-card text-center py-8">
            <p className="text-body text-gray-700 font-medium">No quotes yet</p>
            <p className="mt-1 text-small text-gray-500">
              Tap the + button to create your first quote.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((q) => (
              <li key={q.id}>
                <Link
                  to={`/quotes/${q.id}`}
                  className="mb-card-tap block no-underline"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-body font-semibold text-gray-900 truncate">
                        {q.quote_number ?? `#${q.id}`}
                      </p>
                      <p className="text-small text-gray-700 truncate">
                        {q.customer_name ?? '—'}
                      </p>
                      <p className="mt-1 text-caption text-gray-500">
                        {fmtDate(q.date)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <StatusPill status={q.status} />
                      <p className="text-body font-semibold text-gray-900">
                        {fmt(q.final_quote_price)}
                      </p>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {hasMore && (
          <div className="mt-3 flex justify-center">
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="min-h-touch px-4 text-small text-amber-700 font-medium hover:underline disabled:opacity-60"
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>

      {/* FAB — same pattern as Sales list. */}
      <button
        type="button"
        onClick={() => navigate('/quotes/new')}
        aria-label="New quote"
        className="fixed right-4 h-14 w-14 rounded-full bg-amber-500 hover:bg-amber-600 text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform"
        style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
      >
        <Plus className="h-6 w-6" />
      </button>
    </main>
  );
}
